"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.organizationSchema = exports.groupTypeSchema = exports.visitAttributionModeSchema = void 0;
exports.organizationGroups = organizationGroups;
exports.organizationPractices = organizationPractices;
exports.parseOrganization = parseOrganization;
// `config/organization.yaml` (SPEC §14.2): ACOs, their practices (TINs) and their performance groups.
// This file holds the rules that need only this document: TINs unique within an ACO, group TINs inside
// the ACO's practices (except ACO_ALL_TINS), NPIs passing Luhn. Pathways and measures are checked against
// the measures document in `cross-check.ts`. PI, IA and Cost are reported values (D-994); a group's
// `quality_points_max` overrides its pathway's (D-996).
// J-268 (QB3 item 1b, the owner's order of 2026-10-17): a performance group may hold practices of more
// than one ACO. Such a group is stated in the top-level `performance_groups` list, each with its
// practices (TIN, practice name, ACO code); a practice stated there is a practice of its ACO, so the
// ACO's own `practices` list need not repeat it. The nested shape (`acos[].groups` with `tins`) still
// reads unchanged. Every consumer reads groups and practices through `organizationGroups` and
// `organizationPractices`, which give both shapes as one list.
const zod_1 = require("zod");
const npi_js_1 = require("../npi.js");
const config_issue_js_1 = require("./config-issue.js");
const measures_js_1 = require("./measures.js");
// The value lists of `core.performance_group`'s CHECK constraints (packages/schema/src/db/aco-org.ts).
const GROUP_TYPES = ["GROUP", "INDIVIDUAL", "APM_ENTITY"];
const VISIT_ATTRIBUTION_MODES = ["NPI_ONLY", "NPI_TIN", "TIN_ONLY", "ACO_ALL_TINS"];
const ACO_ALL_TINS = "ACO_ALL_TINS";
/** SPEC §4.3's four modes; the F3 loader checks a file's `Visits` value against it too. */
exports.visitAttributionModeSchema = zod_1.z.enum(VISIT_ATTRIBUTION_MODES);
/** `core.performance_group.group_type`: Screen 1's Type icon reads it (SPEC §7.2). */
exports.groupTypeSchema = zod_1.z.enum(GROUP_TYPES);
const tinSchema = zod_1.z.string().regex(/^\d{9}$/, "TIN must be 9 digits");
const npiSchema = zod_1.z
    .string()
    .refine(npi_js_1.isValidNpi, "NPI must be 10 digits with a valid check digit (Luhn with prefix 80840)");
const reportedPointsSchema = zod_1.z.number().nonnegative().nullable().default(null);
const practiceSchema = zod_1.z.strictObject({
    name: zod_1.z.string().min(1),
    tin: tinSchema,
    is_small_practice: zod_1.z.boolean().default(true),
});
const groupSchema = zod_1.z.strictObject({
    name: zod_1.z.string().min(1),
    type: exports.groupTypeSchema,
    pathway: zod_1.z.string().min(1),
    visit_attribution: exports.visitAttributionModeSchema,
    apm_entity_id: zod_1.z.string().min(1).nullable().default(null),
    tins: zod_1.z.array(tinSchema),
    npis: zod_1.z.array(zod_1.z.strictObject({ npi: npiSchema, tin: tinSchema })).default([]),
    measures: zod_1.z
        .array(zod_1.z.strictObject({
        number: measures_js_1.measureNumberSchema,
        collection_type: measures_js_1.collectionTypeSchema,
        spec_version: zod_1.z.string().min(1).default(measures_js_1.STANDARD_SPEC_VERSION),
    }))
        .min(1),
    display_order: zod_1.z.int().nonnegative(),
    registry_group_label: zod_1.z.string().min(1).nullable().default(null),
    pi_points: reportedPointsSchema,
    ia_points: reportedPointsSchema,
    cost_points: reportedPointsSchema,
    quality_points_max: reportedPointsSchema,
});
/** J-268: one practice of a performance group in `performance_groups`: its TIN, name and ACO code. */
const groupPracticeSchema = zod_1.z.strictObject({
    tin: tinSchema,
    name: zod_1.z.string().min(1),
    aco: zod_1.z.string().min(1),
    is_small_practice: zod_1.z.boolean().default(true),
});
/** J-268: a performance group listed with its practices, which may be in more than one ACO. */
const performanceGroupSchema = groupSchema
    .omit({ tins: true })
    .extend({ practices: zod_1.z.array(groupPracticeSchema).min(1) });
const acoSchema = zod_1.z.strictObject({
    code: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1),
    apm_entity_id: zod_1.z.string().min(1).nullable().default(null),
    platform_name: zod_1.z.string().min(1).nullable().default(null),
    /**
     * The ACO's token in Analysis's bundles (`VBLTC1`, `VBLTC2`; CR-004, J-181): `ref.aco.analysis_code`.
     * A bundle line whose `aco_code` maps to no ACO is refused `UNKNOWN_ACO`. Unset until the ACO is
     * loaded from bundles.
     */
    analysis_code: zod_1.z
        .string()
        .regex(/^[A-Za-z0-9][A-Za-z0-9_-]*$/, "letters, digits, '-' and '_'")
        .nullable()
        .default(null),
    program: zod_1.z.string().min(1),
    create_aco_wide_group: zod_1.z.boolean().default(false),
    aco_wide_group_name: zod_1.z.string().min(1).optional(),
    // J-268: may be empty when `performance_groups` states the ACO's practices (checked below).
    practices: zod_1.z.array(practiceSchema).default([]),
    groups: zod_1.z.array(groupSchema).default([]),
});
function checkPracticeTins(aco, a, ctx) {
    const seen = new Map();
    aco.practices.forEach((practice, p) => {
        const first = seen.get(practice.tin);
        if (first === undefined)
            seen.set(practice.tin, p);
        else {
            ctx.addIssue({
                code: "custom",
                path: ["acos", a, "practices", p, "tin"],
                message: `TIN ${practice.tin} is already used by practices[${first}] in ACO ${aco.code}`,
            });
        }
    });
}
function checkGroupTins(aco, a, performanceGroups, ctx) {
    // J-268: a practice `performance_groups` states for this ACO is one of its practices too.
    const practiceTins = new Set([
        ...aco.practices.map((practice) => practice.tin),
        ...performanceGroups.flatMap((g) => g.practices.filter((p) => p.aco === aco.code).map((p) => p.tin)),
    ]);
    aco.groups.forEach((group, g) => {
        if (group.visit_attribution === ACO_ALL_TINS)
            return;
        group.tins.forEach((tin, t) => {
            if (!practiceTins.has(tin)) {
                ctx.addIssue({
                    code: "custom",
                    path: ["acos", a, "groups", g, "tins", t],
                    message: `TIN ${tin} is not in ACO ${aco.code}`,
                });
            }
        });
    });
}
function checkAcoWideGroupName(aco, a, ctx) {
    if (aco.create_aco_wide_group && aco.aco_wide_group_name === undefined) {
        ctx.addIssue({
            code: "custom",
            path: ["acos", a, "aco_wide_group_name"],
            message: "aco_wide_group_name is required when create_aco_wide_group is true",
        });
    }
}
/**
 * J-268: each `performance_groups` practice names a configured ACO, appears once in its group, and keeps
 * one name per (ACO, TIN) across the document; every ACO has at least one practice in either place.
 */
function checkGroupPractices(acos, groups, ctx) {
    const codes = new Set(acos.map((a) => a.code));
    const names = new Map();
    for (const aco of acos)
        for (const p of aco.practices)
            names.set(`${aco.code}|${p.tin}`, p.name);
    const stated = new Set(names.keys());
    groups.forEach((group, g) => {
        const tins = new Set();
        group.practices.forEach((practice, p) => {
            const at = ["performance_groups", g, "practices", p];
            if (!codes.has(practice.aco)) {
                const message = `ACO ${practice.aco} is not an ACO of this organization`;
                ctx.addIssue({ code: "custom", path: [...at, "aco"], message });
            }
            if (tins.has(practice.tin)) {
                const message = `TIN ${practice.tin} is already in this group`;
                ctx.addIssue({ code: "custom", path: [...at, "tin"], message });
            }
            tins.add(practice.tin);
            const key = `${practice.aco}|${practice.tin}`;
            const known = names.get(key);
            if (known !== undefined && known !== practice.name) {
                const message = `TIN ${practice.tin} in ACO ${practice.aco} is already named ${known}`;
                ctx.addIssue({ code: "custom", path: [...at, "name"], message });
            }
            names.set(key, practice.name);
            stated.add(key);
        });
    });
    acos.forEach((aco, a) => {
        if ([...stated].some((key) => key.startsWith(`${aco.code}|`)))
            return;
        const message = `ACO ${aco.code} has no TIN: list one under practices or performance_groups`;
        ctx.addIssue({ code: "custom", path: ["acos", a, "practices"], message });
    });
}
// `ref.aco.aco_code` and `ref.aco.analysis_code` are unique, and `core.performance_group` is unique on
// (year, group_name).
function checkUniqueNames(acos, performanceGroups, ctx) {
    const codes = new Map();
    const analysisCodes = new Map();
    const groups = new Map();
    acos.forEach((aco, a) => {
        const first = codes.get(aco.code);
        if (first === undefined)
            codes.set(aco.code, a);
        else {
            const message = `ACO code ${aco.code} is already used by acos[${first}]`;
            ctx.addIssue({ code: "custom", path: ["acos", a, "code"], message });
        }
        if (aco.analysis_code !== null) {
            const owner = analysisCodes.get(aco.analysis_code);
            if (owner === undefined)
                analysisCodes.set(aco.analysis_code, a);
            else {
                const message = `analysis code ${aco.analysis_code} is already used by acos[${owner}]`;
                ctx.addIssue({ code: "custom", path: ["acos", a, "analysis_code"], message });
            }
        }
        aco.groups.forEach((group, g) => {
            const owner = groups.get(group.name);
            if (owner === undefined)
                groups.set(group.name, `acos[${a}].groups[${g}]`);
            else {
                const message = `group name ${group.name} is already used by ${owner}`;
                ctx.addIssue({ code: "custom", path: ["acos", a, "groups", g, "name"], message });
            }
        });
    });
    performanceGroups.forEach((group, g) => {
        const owner = groups.get(group.name);
        if (owner === undefined)
            groups.set(group.name, `performance_groups[${g}]`);
        else {
            const message = `group name ${group.name} is already used by ${owner}`;
            ctx.addIssue({ code: "custom", path: ["performance_groups", g, "name"], message });
        }
    });
}
exports.organizationSchema = zod_1.z
    .strictObject({
    performance_year: zod_1.z.int().min(1000).max(9999),
    acos: zod_1.z.array(acoSchema).min(1),
    /** J-268: performance groups listed with their practices, which may be in more than one ACO. */
    performance_groups: zod_1.z.array(performanceGroupSchema).default([]),
})
    .superRefine((org, ctx) => {
    org.acos.forEach((aco, a) => {
        checkPracticeTins(aco, a, ctx);
        checkGroupTins(aco, a, org.performance_groups, ctx);
        checkAcoWideGroupName(aco, a, ctx);
    });
    checkGroupPractices(org.acos, org.performance_groups, ctx);
    checkUniqueNames(org.acos, org.performance_groups, ctx);
});
/** Every configured group, the nested ones first in ACO order, then `performance_groups` (J-268). */
function organizationGroups(org) {
    const nested = org.acos.flatMap((aco, a) => aco.groups.map(({ tins, ...group }, g) => ({
        ...group,
        practices: tins.map((tin) => ({ tin, acoCode: aco.code })),
        path: ["acos", a, "groups", g],
    })));
    const listed = org.performance_groups.map(({ practices, ...group }, g) => ({
        ...group,
        practices: practices.map((p) => ({ tin: p.tin, acoCode: p.aco })),
        path: ["performance_groups", g],
    }));
    return [...nested, ...listed];
}
/**
 * Every practice of every ACO, each (ACO, TIN) once: the ACO's own list first, then the practices only
 * `performance_groups` states, in the order they appear (J-268).
 */
function organizationPractices(org) {
    const out = new Map();
    for (const aco of org.acos)
        for (const p of aco.practices)
            out.set(`${aco.code}|${p.tin}`, {
                acoCode: aco.code,
                tin: p.tin,
                name: p.name,
                isSmallPractice: p.is_small_practice,
            });
    for (const group of org.performance_groups)
        for (const p of group.practices) {
            const key = `${p.aco}|${p.tin}`;
            if (!out.has(key))
                out.set(key, {
                    acoCode: p.aco,
                    tin: p.tin,
                    name: p.name,
                    isSmallPractice: p.is_small_practice,
                });
        }
    return [...out.values()];
}
/** Parses `config/organization.yaml`; issues name the document `organization`. */
function parseOrganization(input) {
    return (0, config_issue_js_1.parseConfigDocument)(exports.organizationSchema, "organization", input);
}
//# sourceMappingURL=organization.js.map